import {
  AcknowledgementName,
  AcknowledgementStatus,
  Configuration,
  PersonInviteRedemption,
  PersonsApi
} from "@wingspanhq/wingspan-api-sdk";
import { acknowledgementsApi, personsApi } from "@api";
import { config } from "@config";
import { sessionService } from "@services/sessionService";
import { useWSMutation, WSQueryCache } from "@ws-react-query";
import { QUERY_USERS_SESSION } from "../../../query/session";
import { useMutationCreateSession } from "../../../query/users/mutations";
import { acknowledgementVersions } from "../../../shared/constants/acknowledgements";

const SIGN_UP_CONSENTS: AcknowledgementName[] = [
  AcknowledgementName.WINGSPAN_TOS_ACCEPTANCE,
  AcknowledgementName.WINGSPAN_PRIVACY_POLICY_ACCEPTANCE,
  AcknowledgementName.ELECTRONIC_DISCLOSURE_AND_CONSENT
];

export type CompleteRegistrationData = {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  remember: boolean;
};

export const useMutationCompleteRegistration = (
  redemption: PersonInviteRedemption
) => {
  const [createV1Session] = useMutationCreateSession();

  return useWSMutation<void, unknown, CompleteRegistrationData>(
    async data => {
      const redemptionPersonsApi = new PersonsApi(
        new Configuration({
          basePath: config.apiBaseUrl,
          accessToken: () => redemption.sessionToken
        })
      );
      await redemptionPersonsApi.resetPersonPassword({
        personId: redemption.personId,
        resetPersonPasswordRequest: {
          token: redemption.sessionToken,
          newPassword: data.password
        }
      });

      await sessionService.login(data.email, data.password, data.remember);

      await personsApi.submitPersonAsSelf({
        personSelfSubmission: {
          person: {
            firstName: data.firstName,
            lastName: data.lastName
          }
        }
      });

      await Promise.all(
        SIGN_UP_CONSENTS.map(async acknowledgementName => {
          const acknowledgement = await acknowledgementsApi.getAcknowledgement({
            acknowledgementName
          });
          if (
            acknowledgement.acknowledgementStatus !==
            AcknowledgementStatus.GIVEN
          ) {
            await acknowledgementsApi.acceptAcknowledgement({
              acknowledgementName,
              acceptAcknowledgementRequest: {
                version: acknowledgementVersions[acknowledgementName]
              }
            });
          }
        })
      );

      const session = await createV1Session(
        { email: data.email, password: data.password, remember: data.remember },
        { throwOnError: true }
      );
      if (session) {
        WSQueryCache.setQueryData(QUERY_USERS_SESSION, session);
      }
    },
    { dependencies: [QUERY_USERS_SESSION] }
  );
};
